/* Copyright (c) 2022-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable } from 'rxjs';
import { ApplicationControl, FileHandle, RequestedApplicationControl } from 'tizen-common-web';
import { WebApis } from './samsung-webapis';

export const TizenKeyCodes = {
    Done: 65376
} as const;

enum TizenAppControlOperation {
    /** An operation used by GameHub to open deeplink */
    EdenResume = 'http://samsung.com/appcontrol/operation/eden_resume'
}

export enum TizenDeviceCapabilityKey {
    PlatformCoreCpuArch = 'http://tizen.org/feature/platform.core.cpu.arch',
    ModelName = 'http://tizen.org/system/model_name'
}

/** Checks if `tizen` feature exists. */
export function isTizenAPIAvailable(): boolean {
    return 'tizen' in window;
}

/**
 * Gets the PAYLOAD from the `appControl` with {@link TizenAppControlOperation.EdenResume} operation.
 * The payload is a `string` whose format and content is defined by us. Currently expecting it to be of following format:
 *
 * @example '/deeplink?game-id=d6904ab8-358b-4112-b4fc-1a1c0a0ecad2&action=view-game'
 * @return the payload, `undefined` if not found.
 */
export function getEdenResumePayload(appControl: ApplicationControl): string {
    if (appControl.operation !== TizenAppControlOperation.EdenResume) {
        return undefined;
    }
    return appControl.data.find(d => d.key === 'PAYLOAD')?.value[0];
}

/** Shorthand for `tizen?.application.getCurrentApplication().getRequestedAppControl()` */
export function getRequestedAppControl(): RequestedApplicationControl {
    return tizen?.application.getCurrentApplication().getRequestedAppControl();
}

export function exitTizenApplication(): void {
    tizen?.application.getCurrentApplication().exit();
}

/**
 * systeminfo.getCapabilities() is deprecated since 2.3.
 * Instead, use getCapability() with TizenDeviceCapabilityKey.
 *
 * @note it will throw error if the capability is not supported, caller needs to handle it.
 */
export function getTizenCapability<T>(key: TizenDeviceCapabilityKey): T {
    return tizen?.systeminfo.getCapability(key);
}

function getWebApis(): WebApis {
    // Casting to any to WAR Ragnarok's restricted type definition
    return (window as any).webapis;
}

export function getTizenChipsetName(): string {
    const modelCode = getWebApis()?.productinfo?.getModelCode();
    return modelCode?.split('_')?.[1];
}

export function getTizenIsUdPanelSupported(): boolean {
    return getWebApis()?.productinfo?.isUdPanelSupported();
}

export function getTizenTVServerVersion(): string {
    return getWebApis()?.productinfo?.getSmartTVServerVersion();
}

export function parseTizenTVServerVersion(serverVersion: string): { year?: string; version?: string } {
    if (!serverVersion) {
        return {};
    }

    const [year, version] = serverVersion.split('T-INFOLINK')[1]?.split('-');
    return { year, version };
}

/**
 * Write content to target file in Tizen file system
 *
 * @param filePath target file path
 * @param content string that will be written in file
 * @returns number of bytes of written content
 */
export function writeStringToTizenFileSystem(filePath: string, content: string): Observable<number> {
    let fileHandle: FileHandle = null;
    const write$ = new Observable<number>(subscriber => {
        // Prevent emitting value when callbacks from Tizen API after subscriber unsubscribes.
        let subscribe = true;
        fileHandle = tizen.filesystem.openFile(filePath, 'w');
        fileHandle.writeStringNonBlocking(
            content,
            bytesCount => {
                if (subscribe) {
                    subscriber.next(bytesCount);
                    subscriber.complete();
                    fileHandle?.closeNonBlocking();
                }
            },
            error => {
                if (subscribe) {
                    // Samsung suggested we emit error.name instead of entire error object.
                    subscriber.error(error.name);
                    subscriber.complete();
                    fileHandle?.closeNonBlocking();
                }
            }
        );
        // unsubscribe by subscribers
        return () => {
            subscribe = false;
            fileHandle?.closeNonBlocking();
        };
    });
    return write$;
}
